/**
 * Whether the app can still reach the API.
 *
 * Someone exported ten clips and then watched a page that polled every few
 * seconds for eighteen minutes: 198 requests in a row, every one of them a
 * network failure, and the page said nothing at all. Their clips were finishing
 * the whole time. They left without them.
 *
 * A single failure is not worth a word — a poll drops, the next one works. A
 * run of them means the person is looking at a screen that has stopped being
 * true, and they should be told rather than left to guess.
 */

// Three in a row: long enough that one dropped poll says nothing, short enough
// to show up within a poll cycle or two rather than after minutes of silence
export const FAILURES_BEFORE_LOST = 3;

let failures = 0;
let lost = false;
const listeners = new Set();

// One bad listener never stops the others, and never breaks the request that
// was only reporting on its way through
const tell = (listener) => {
  try {
    listener(lost);
  } catch {
    /* nothing to do about someone else's render */
  }
};

const announce = () => {
  for (const listener of listeners) tell(listener);
};

/** A request that reached the server, whatever it answered: we are connected. */
export function reportReachable() {
  failures = 0;
  if (lost) {
    lost = false;
    announce();
  }
}

/** A request that got no answer at all (no response, or the server is down). */
export function reportUnreachable() {
  failures += 1;
  if (!lost && failures >= FAILURES_BEFORE_LOST) {
    lost = true;
    announce();
  }
}

export const isConnectionLost = () => lost;

/**
 * What one failed request says about the connection.
 *
 * A refusal or a 500 is still an answer — the server is there and talking, and
 * a page full of 403s is not a lost connection. A request that came back with
 * nothing is the one that counts. A cancelled request says neither: the app
 * gave up on it, the network did not, and treating those as failures would put
 * the banner up every time someone changed page mid-poll.
 */
export function noteRequestFailure(error) {
  if (error?.response) {
    reportReachable();
    return;
  }
  const cancelled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.__CANCEL__;
  if (!cancelled) reportUnreachable();
}

/** Call with the current state, and on every change. Returns an unsubscribe. */
export function watchConnection(listener) {
  listeners.add(listener);
  tell(listener);
  return () => listeners.delete(listener);
}

// Tests only: nothing in the app resets this
export function __reset() {
  failures = 0;
  lost = false;
  listeners.clear();
}
